import { PrepaidBookingPricesMyMSCResponse } from '@msc-b2c/interfaces/interfaces';
import { EcouponErrorMessageConfig } from '@msc-b2c/interfaces/types';
import {
  extractEcouponIssueCodeFromError,
  isConfiguredEcouponErrorCode,
  normalizeEcouponErrorCode,
} from './ecouponErrorUtils';

export type ItemLevelCouponOutcome = {
  hasAtLeastOneApplicableItem: boolean;
  allItemsNotApplicable: boolean;
  commonReasonCode?: string;
};

export const getItemLevelCouponOutcome = (
  response: PrepaidBookingPricesMyMSCResponse
): ItemLevelCouponOutcome => {
  const responseItems = [...(response.excursions || []), ...(response.onboardServices || [])];
  if (responseItems.length === 0) {
    return { hasAtLeastOneApplicableItem: false, allItemsNotApplicable: false };
  }

  const itemOutcomes = responseItems.map((item) => {
    const couponEntries = [
      ...(item.passengers?.map((passenger) => passenger.couponCode) || []),
      ...(item.cabins?.map((cabin) => cabin.couponCode) || []),
    ].filter((couponCode) => couponCode && typeof couponCode.isApplicable === 'boolean');

    const hasApplicable = couponEntries.some((couponCode) => couponCode?.isApplicable === true);
    const isNotApplicable = couponEntries.length > 0 && !hasApplicable;
    const reasonCodes = couponEntries
      .filter((couponCode) => couponCode?.isApplicable === false)
      .map((couponCode) => couponCode?.reason?.code?.trim())
      .filter((reasonCode): reasonCode is string => Boolean(reasonCode));

    return { hasApplicable, isNotApplicable, reasonCodes };
  });

  const hasAtLeastOneApplicableItem = itemOutcomes.some((item) => item.hasApplicable);
  const allItemsNotApplicable = itemOutcomes.every((item) => item.isNotApplicable);

  if (!allItemsNotApplicable) {
    return { hasAtLeastOneApplicableItem, allItemsNotApplicable };
  }

  const allReasonCodes = itemOutcomes.flatMap((item) => item.reasonCodes);
  const hasReasonForEveryItem = itemOutcomes.every((item) => item.reasonCodes.length > 0);
  if (!hasReasonForEveryItem || allReasonCodes.length === 0) {
    return { hasAtLeastOneApplicableItem, allItemsNotApplicable };
  }

  const uniqueReasonCodes = new Set(allReasonCodes.map((reasonCode) => reasonCode.toLowerCase()));
  return {
    hasAtLeastOneApplicableItem,
    allItemsNotApplicable,
    commonReasonCode: uniqueReasonCodes.size === 1 ? Array.from(uniqueReasonCodes)[0] : undefined,
  };
};

type EcouponErrorConfigSource = {
  ecouponErrorMessageConfigList?:
    | EcouponErrorMessageConfig[]
    | Record<string, EcouponErrorMessageConfig>;
  ecouponErrorMessageConfigListResources?: EcouponErrorMessageConfig[];
};

const isConfiguredCode = (code: string | undefined, config: EcouponErrorConfigSource): boolean =>
  isConfiguredEcouponErrorCode(
    code,
    config.ecouponErrorMessageConfigList,
    config.ecouponErrorMessageConfigListResources
  );

/** Resolves a configured e-coupon issue code when the applied coupon is no longer valid in prices. */
export const resolveInvalidAppliedCouponIssueCode = (
  response: PrepaidBookingPricesMyMSCResponse,
  config: EcouponErrorConfigSource
): string | undefined => {
  const warningCode = normalizeEcouponErrorCode(response?._warnings?.[0]?.code);
  if (warningCode && isConfiguredCode(warningCode, config)) {
    return warningCode;
  }

  const { hasAtLeastOneApplicableItem, commonReasonCode } = getItemLevelCouponOutcome(response);
  if (hasAtLeastOneApplicableItem) {
    return undefined;
  }

  const normalizedCommon = normalizeEcouponErrorCode(commonReasonCode);
  if (normalizedCommon && isConfiguredCode(normalizedCommon, config)) {
    return normalizedCommon;
  }

  return undefined;
};

export const resolveInvalidAppliedCouponIssueCodeFromError = (
  error: unknown,
  config: EcouponErrorConfigSource
): string | undefined => {
  const issueCode = normalizeEcouponErrorCode(extractEcouponIssueCodeFromError(error));
  if (issueCode && isConfiguredCode(issueCode, config)) {
    return issueCode;
  }
  return undefined;
};

export const isAppliedCouponStillValid = (response: PrepaidBookingPricesMyMSCResponse): boolean =>
  getItemLevelCouponOutcome(response).hasAtLeastOneApplicableItem;
